import Head from '@/components/Head';
import { DATADOME_JS, DATADOME_TAGS } from '@gik/analytics/utils/DataDome';
import { PageHeadKeys } from '@gik/core/hocs/PageHeadKeys';
import { generateTitle } from '@gik/core/hocs/withTitle';
import { useEnvStore } from '@gik/core/store/EnvStore';
import { useRouter } from 'next/router';
import Script from 'next/script';
import React from 'react';

export function BaseHeadTags() {
  const router = useRouter();
  const vercelEnv = useEnvStore(state => state.VERCEL_ENV);
  const vercelURL = useEnvStore(state => state.VERCEL_URL);

  const URL = React.useMemo(
    () => (vercelEnv == 'production' ? 'https://www.giveinkind.com' : vercelURL ? `https://${vercelURL}` : ''),
    [vercelEnv, vercelURL]
  );
  const href = React.useMemo(() => `${URL}${router.asPath != '/index' ? router.asPath : ''}`, [URL, router.asPath]);
  const dateNow = React.useMemo(() => new Date().toISOString(), []);
  const pathToFavicons = React.useMemo(() => '/core/img/favicon.ico', []);

  const description = React.useMemo(
    () =>
      'Set up a Give InKind Page to coordinate support from anywhere. Create a' +
      ' Care Calendar, add items to your Wishlist and connect your Crowdfunding - all for free.',
    []
  );

  return (
    <Head>
      <title key={PageHeadKeys.TITLE}>{generateTitle()}</title>

      <meta charSet="utf-8" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="HandheldFriendly" content="True" />
      <meta name="referrer" content="no-referrer-when-downgrade" />

      {/* MS */}
      <meta name="application-name" content="GiveInKind" />
      <meta name="msapplication-TileColor" content="#ffffff" />
      <meta name="msapplication-TileImage" content={pathToFavicons + '/ms-icon-144x144.png'} />

      {/* theme-color defines the top bar color */}
      <meta name="theme-color" content="#ffffff" />

      {/* Favicon */}
      <link rel="icon" type="image/png" sizes="192x192" href={pathToFavicons + '/android-icon-192x192.png'} />
      <link rel="icon" type="image/png" sizes="32x32" href={pathToFavicons + '/favicon-32x32.png'} />
      <link rel="icon" type="image/png" sizes="96x96" href={pathToFavicons + '/favicon-96x96.png'} />
      <link rel="icon" type="image/png" sizes="16x16" href={pathToFavicons + '/favicon-16x16.png'} />
      <link rel="shortcut icon" href={pathToFavicons + '/favicon.ico'} type="image/png" />

      {/* Apple */}
      <link rel="apple-touch-icon" sizes="57x57" href={pathToFavicons + '/apple-icon-57x57.png'} />
      <link rel="apple-touch-icon" sizes="60x60" href={pathToFavicons + '/apple-icon-60x60.png'} />
      <link rel="apple-touch-icon" sizes="72x72" href={pathToFavicons + '/apple-icon-72x72.png'} />
      <link rel="apple-touch-icon" sizes="76x76" href={pathToFavicons + '/apple-icon-76x76.png'} />
      <link rel="apple-touch-icon" sizes="114x114" href={pathToFavicons + '/apple-icon-114x114.png'} />
      <link rel="apple-touch-icon" sizes="120x120" href={pathToFavicons + '/apple-icon-120x120.png'} />
      <link rel="apple-touch-icon" sizes="144x144" href={pathToFavicons + '/apple-icon-144x144.png'} />
      <link rel="apple-touch-icon" sizes="152x152" href={pathToFavicons + '/apple-icon-152x152.png'} />
      <link rel="apple-touch-icon" sizes="180x180" href={pathToFavicons + '/apple-icon-180x180.png'} />

      {/* Facebook */}
      <meta property="fb:app_id" content="928334690612401" />

      <meta property="og:locale" content="en_US" />
      <meta property="og:type" key={PageHeadKeys.META_OG_TYPE} content="website" />
      <meta
        key={PageHeadKeys.META_OG_TITLE}
        property="og:title"
        content="Give InKind | Connecting People During Times of Need"
      />
      <meta key={PageHeadKeys.META_OG_DESCRIPTION} property="og:description" content={description} />
      <meta property="og:site_name" content="Give InKind" />

      {/* Twitter */}
      <meta name="twitter:card" key={PageHeadKeys.META_TWITTER_CARD} content="summary_large_image" />
      <meta name="twitter:site" content="@giveinkind" />
      <meta name="twitter:description" key={PageHeadKeys.META_TWITTER_DESCRIPTION} content={description} />
      <meta
        name="twitter:title"
        content="Give InKind | Connecting People During Times of Need"
        key={PageHeadKeys.META_TWITTER_TITLE}
      />
      <meta
        property="twitter:image"
        content={`${URL}/homepage/img/inkind-og-image.png`}
        key={PageHeadKeys.META_TWITTER_IMAGE}
      />

      <link rel="canonical" href={href} key={PageHeadKeys.LINK_CANONICAL} />
      <meta property="og:url" content={href} key={PageHeadKeys.META_OG_URL} />
      <meta property="og:image" content={`${URL}/homepage/img/inkind-og-image.png`} key={PageHeadKeys.META_OG_IMAGE} />
      <meta
        property="og:image:secure_url"
        content={`${URL}/homepage/img/inkind-og-image.png`}
        key={PageHeadKeys.META_OG_IMAGE_SECURE_URL}
      />
      <meta property="og:image:width" content="1200" key={PageHeadKeys.META_OG_IMAGE_WIDTH} />
      <meta property="og:image:height" content="627" key={PageHeadKeys.META_OG_IMAGE_HEIGHT} />

      <meta property="og:updated_time" content={dateNow} key={PageHeadKeys.META_OG_UPDATED_TIME} />
      <meta name="description" key={PageHeadKeys.META_DESCRIPTION} content={description} />

      {/* inkind-dev.vercel.app */}
      <meta
        name="google-site-verification"
        key={PageHeadKeys.GOOGLE_SITE_VERIFICATION_DEV_VERCEL_DOMAIN}
        content="unNxLIkepWvtFGqu2rS2CiEbycOYCuO3fnXkRBfdDr8"
      />
      {/* www.inkind.dev */}
      <meta
        name="google-site-verification"
        key={PageHeadKeys.GOOGLE_SITE_VERIFICATION_DEV_DOMAIN}
        content="2KItrsMNSrdSLAtckuqZmZ7CNHdW5MG4KvqVD5jqXuI"
      />

      <script src="https://apis.google.com/js/platform.js" async defer key={PageHeadKeys.GOOGLE_PLATFORM} />

      {process.env.NEXT_PUBLIC_DATADOME_CLIENT_SIDE_KEY && (
        <>
          {/* datadome bot protection */}
          <Script key={PageHeadKeys.DATADOME_JS} strategy="lazyOnload" id="load-datadome">{`
        window.ddjskey = '${process.env.NEXT_PUBLIC_DATADOME_CLIENT_SIDE_KEY}'
        window.ddoptions = {
          endpoint: '${DATADOME_JS}'
        }
      `}</Script>
          <Script key={PageHeadKeys.DATADOME_TAGS} src={DATADOME_TAGS} strategy="lazyOnload" />
        </>
      )}

      <link rel="preconnect" href="https://dev.visualwebsiteoptimizer.com" key={PageHeadKeys.VWO_LINK} />
      <script
        type="text/javascript"
        id="vwoCode"
        key={PageHeadKeys.VWO_SCRIPT}
        strategy={'beforeInteractive'}
        dangerouslySetInnerHTML={{
          __html: `
          window._vwo_code || (function() {
          var account_id=744479,
          version=2.1,
          settings_tolerance=2000,
          hide_element='body',
          hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important;transition:none !important;',
          /* DO NOT EDIT BELOW THIS LINE */
          f=false,w=window,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}var stT=cc.stT==='session'?w.sessionStorage:w.localStorage;code={nonce:v&&v.nonce,use_existing_jquery:function(){return typeof use_existing_jquery!=='undefined'?use_existing_jquery:undefined},library_tolerance:function(){return typeof library_tolerance!=='undefined'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return'{'+(cc.hES||hide_element_style)+'}'},hide_element:function(){if(performance.getEntriesByName('first-contentful-paint')[0]){return''}return typeof cc.hE==='string'?cc.hE:hide_element},getVersion:function(){return version},finish:function(e){if(!f){f=true;var t=d.getElementById('_vis_opt_path_hides');if(t)t.parentNode.removeChild(t);if(e)(new Image).src='https://dev.visualwebsiteoptimizer.com/ee.gif?a='+account_id+e}},finished:function(){return f},addScript:function(e){var t=d.createElement('script');t.type='text/javascript';if(e.src){t.src=e.src}else{t.text=e.text}v&&t.setAttribute('nonce',v.nonce);d.getElementsByTagName('head')[0].appendChild(t)},load:function(e,t){var n=this.getSettings(),i=d.createElement('script'),r=this;t=t||{};if(n){i.textContent=n;d.getElementsByTagName('head')[0].appendChild(i);if(!w.VWO||VWO.caE){stT.removeItem(cK);r.load(e)}}else{var o=new XMLHttpRequest;o.open('GET',e,true);o.withCredentials=!t.dSC;o.responseType=t.responseType||'text';o.onload=function(){if(t.onloadCb){return t.onloadCb(o,e)}if(o.status===200||o.status===304){w._vwo_code.addScript({text:o.responseText})}else{w._vwo_code.finish('&e=loading_failure:'+e)}};o.onerror=function(){if(t.onerrorCb){return t.onerrorCb(e)}w._vwo_code.finish('&e=loading_failure:'+e)};o.send()}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;var e=this.settings_tolerance();w._vwo_settings_timer=setTimeout(function(){w._vwo_code.finish();stT.removeItem(cK)},e);var t;if(this.hide_element()!=='body'){t=d.createElement('style');var n=this.hide_element(),i=n?n+this.hide_element_style():'',r=d.getElementsByTagName('head')[0];t.setAttribute('id','_vis_opt_path_hides');v&&t.setAttribute('nonce',v.nonce);t.setAttribute('type','text/css');if(t.styleSheet)t.styleSheet.cssText=i;else t.appendChild(d.createTextNode(i));r.appendChild(t)}else{t=d.getElementsByTagName('head')[0];var i=d.createElement('div');i.style.cssText='z-index: 2147483647 !important;position: fixed !important;left: 0 !important;top: 0 !important;width: 100% !important;height: 100% !important;background: white !important;display: block !important;';i.setAttribute('id','_vis_opt_path_hides');i.classList.add('_vis_hide_layer');t.parentNode.insertBefore(i,t.nextSibling)}var o=window._vis_opt_url||d.URL,s='https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(o)+'&vn='+version;if(w.location.search.indexOf('_vwo_xhr')!==-1){this.addScript({src:s})}else{this.load(s+'&x=true')}}};w._vwo_code=code;code.init();})();(function(){var i=window;function t(){if(i._vwo_code){var e=t.hidingStyle=document.getElementById('_vis_opt_path_hides')||t.hidingStyle;if(!i._vwo_code.finished()&&!_vwo_code.libExecuted&&(!i.VWO||!VWO.dNR)){if(!document.getElementById('_vis_opt_path_hides')){document.getElementsByTagName('head')[0].appendChild(e)}requestAnimationFrame(t)}}}t()})();
        `,
        }}
      />
    </Head>
  );
}
